/**
 * Zustand store for the AMATL job.
 *
 * One `Job` object owns everything the UI shows (part, cell, operations,
 * outputs). The dependency graph lives inline — editing an upstream
 * item marks dependent operations `needsRegen` via the mutation helpers
 * below.
 *
 * Phase 1 ships the shape + stub methods. Phases 2+ wire real backend
 * calls (uploadStep → classify → generate → ik → collision → rapid) and
 * .amatl.json save/load.
 */
import { create, type StoreApi, type UseBoundStore } from 'zustand'

import * as api from './api'
import type { WeldingConfig } from './api'

// ---- types ---------------------------------------------------------------

export type Part = {
  upload_id: string
  filename: string
  face_count: number
  tessellation_url: string | null // GLB served by backend
}

export type ToolDefinition = {
  id: string
  vendor: string
  part: string
  description: string
  version: number
  mass_kg: number | null
  stickout_mm: number
  neck: { angle_to_flange_axis_deg: number; axis_dir: [number, number, number] }
  mount: { bolt_pcd_mm: number }
  tcp: { pos: [number, number, number]; quat_wxyz: [number, number, number, number] }
  visual_url?: string | null
  step_to_flange_quat_wxyz?: [number, number, number, number] | null
}

export type Cell = {
  robot: string | null
  positioner: string | null
  tool: ToolDefinition | null
  tool_length_m: number
}

export type OperationKind = 'clad' | 'seam' | 'crack' | 'pattern'
export type RegenState = 'ok' | 'needsRegen' | 'running' | 'error'

export type Operation = {
  id: string
  kind: OperationKind
  label: string
  suppressed: boolean
  regenState: RegenState
  faces: number[]
  seamPairs: { a: number; b: number }[]
  wps: WeldingConfig
  // Populated after generate:
  toolpath?: unknown
  trajectory?: unknown
  rapid?: string
  collision?: { collisionCount: number; firstFrame?: number }
}

export type Job = {
  name: string
  part: Part | null
  cell: Cell
  operations: Operation[]
}

export type Selection =
  | { kind: 'part' }
  | { kind: 'cell' }
  | { kind: 'tool' }
  | { kind: 'wobj' }
  | { kind: 'operation'; id: string }

export type Message = {
  id: string
  intent: 'warning' | 'danger' | 'primary' | 'none' | 'success'
  kind: string
  body: string
  frame: number
}

export type Preferences = {
  defaultWps: WeldingConfig
  theme: 'dark' | 'light'
  fontSize: number
}

// ---- defaults ------------------------------------------------------------

const DEFAULT_WPS: WeldingConfig = {
  bead_width_mm: 8.0,
  bead_overlap_pct: 40.0,
  bead_height_mm: 3.0,
  travel_mm_min: 300.0,
  stickout_mm: 22.0,
  wire_dia_mm: 1.2,
  current_a: 250.0,
  voltage_v: 28.0,
  wire_feed_m_min: 8.0,
  layers: 1,
  radial_passes: 1,
  samples_per_rev: 36,
  lead_angle_deg: 0.0,
  torch_offset_mm: 5.0,
  emit_arc: false,
  worm_axial_len_mm: 240.0,
  worm_r_min_mm: 115.0,
  worm_r_max_mm: 123.4,
}

const EMPTY_JOB: Job = {
  name: 'untitled',
  part: null,
  cell: {
    robot: 'IRB 4600-40/2.55',
    positioner: '2-axis positioner (worm cell)',
    tool: null,
    tool_length_m: 0.372,
  },
  operations: [],
}

// ---- store ---------------------------------------------------------------

export type Store = {
  job: Job
  selection: Selection | null
  pickMode: string | null // operation id while picking faces

  toolLibrary: ToolDefinition[]
  preferences: Preferences

  messages: Message[]
  scrubFrame: number
  totalFrames: number
  playing: boolean
  playSpeed: number
  apiConnected: boolean

  hasAnyToolpath: boolean
  hasAnyTrajectory: boolean

  // Actions (Phase 1: stubs that mutate state locally; Phase 2 adds API calls)
  openStepDialog: () => void
  clearPart: () => void
  newOperation: (kind: OperationKind) => void
  /** Create a single Clad operation populated with all faces the batch
   * classifier picks for ``profile`` (default: worm). "Bulk" because
   * one operation covers every outside flank; power users can edit the
   * face list afterwards or split into sub-operations.
   *
   * Phase 2D: ``tiltDeg`` sets the per-operation 2-axis positioner tilt
   * so "top of flight" and "bottom of flight" presets can create two
   * ops that share one RAPID module with different eax_b values. */
  newCladBulk: (
    profile?: 'worm' | 'auger',
    tiltDeg?: number | null,
    labelOverride?: string,
  ) => Promise<void>
  generateAll: () => Promise<void>
  generateOne: (opId: string) => Promise<void>
  exportArtifact: (kind: 'toolpath' | 'trajectory' | 'collision' | 'rapid') => void
  saveJob: () => void
  openJobDialog: () => void
  openPrefs: () => void
  openAbout: () => void
  openToolImport: () => void

  selectTreeNode: (nodeId: string) => void
  togglePickMode: (opId: string) => void
  updateOperation: (opId: string, patch: Partial<Operation>) => void
  commitPendingOperation: () => void
  cancelPendingOperation: () => void
  duplicateOperation: (opId: string) => void
  toggleSuppress: (opId: string) => void
  deleteOperation: (opId: string) => void

  setActiveTool: (toolId: string | null) => void
  updateTool: (toolId: string, patch: Partial<ToolDefinition>) => void

  togglePlay: () => void
  stepFrame: (delta: number) => void
  setScrubFrame: (frame: number) => void
  setPlaySpeed: (speed: number) => void
  jumpToMessage: (messageId: string) => void

  loadToolLibrary: () => Promise<void>
  pingApi: () => Promise<void>

  setPreferences: (prefs: Preferences) => void
  openDialog: 'prefs' | 'about' | 'toolImport' | null
  closeDialog: () => void

  pickFace: (faceIndex: number) => void
  _seamPending: number | null
  _playRaf: number | null

  uploadingStep: boolean

  _setJob: (job: Job) => void
}

const _idSeq = { n: 1 }
const newId = (prefix: string) => `${prefix}_${_idSeq.n++}_${Date.now().toString(36)}`

export const useStore: UseBoundStore<StoreApi<Store>> = create<Store>((set, get) => ({
  job: EMPTY_JOB,
  selection: null,
  pickMode: null,
  toolLibrary: [],
  preferences: { defaultWps: DEFAULT_WPS, theme: 'dark', fontSize: 13 },
  messages: [],
  scrubFrame: 0,
  totalFrames: 0,
  playing: false,
  playSpeed: 1,
  apiConnected: false,
  hasAnyToolpath: false,
  hasAnyTrajectory: false,
  openDialog: null,
  _seamPending: null,
  _playRaf: null,
  uploadingStep: false,

  openStepDialog: () => {
    const inp = document.createElement('input')
    inp.type = 'file'
    inp.accept = '.step,.stp,.STEP,.STP'
    inp.onchange = async () => {
      const f = inp.files?.[0]
      if (!f) return
      set({ uploadingStep: true })
      try {
        const part = await api.uploadStep(f)
        set((s) => ({
          job: { ...s.job, part, operations: markAllNeedsRegen(s.job.operations) },
          selection: { kind: 'part' },
        }))
      } catch (err) {
        console.error(err)
      } finally {
        set({ uploadingStep: false })
      }
    }
    inp.click()
  },

  clearPart: () =>
    set((s) => ({ job: { ...s.job, part: null, operations: [] }, selection: null })),

  newOperation: (kind) =>
    set((s) => {
      const op: Operation = {
        id: newId('op'),
        kind,
        label: `${kind === 'clad' ? 'Clad' : kind === 'seam' ? 'Seam' : kind} ${
          s.job.operations.filter((o) => o.kind === kind).length + 1
        }`,
        suppressed: false,
        regenState: 'needsRegen',
        faces: [],
        seamPairs: [],
        wps: { ...s.preferences.defaultWps },
      }
      return {
        job: { ...s.job, operations: [...s.job.operations, op] },
        selection: { kind: 'operation', id: op.id },
      }
    }),

  newCladBulk: async (profile = 'worm', tiltDeg = null, labelOverride) => {
    const s = get()
    if (!s.job.part) return
    try {
      const res = await api.autoSelectFaces(s.job.part.upload_id, profile)
      if (res.included_faces.length === 0) {
        set((st) => ({
          messages: [...st.messages, {
            id: newId('msg'),
            intent: 'warning',
            kind: 'auto-select',
            body: `Classifier found no cladding faces (profile=${profile}). ` +
                  `Open the console to inspect the per-face reasons.`,
            frame: 0,
          }],
        }))
        return
      }
      set((st) => {
        const op: Operation = {
          id: newId('op'),
          kind: 'clad',
          label: labelOverride ?? `Clad outside (${res.included_faces.length} faces)`,
          suppressed: false,
          regenState: 'needsRegen',
          faces: res.included_faces,
          seamPairs: [],
          wps: {
            ...st.preferences.defaultWps,
            positioner_tilt_deg: tiltDeg,
          },
        }
        return {
          job: { ...st.job, operations: [...st.job.operations, op] },
          selection: { kind: 'operation', id: op.id },
          messages: [...st.messages, {
            id: newId('msg'),
            intent: 'success',
            kind: 'auto-select',
            body: `Picked ${res.included_faces.length} outside face(s) ` +
                  `for cladding; ${res.excluded_faces.length} excluded.`,
            frame: 0,
          }],
        }
      })
    } catch (err) {
      set((st) => ({
        messages: [...st.messages, {
          id: newId('msg'),
          intent: 'danger',
          kind: 'auto-select',
          body: `Auto-select failed: ${err instanceof Error ? err.message : String(err)}`,
          frame: 0,
        }],
      }))
    }
  },

  generateAll: async () => {
    const ops = get().job.operations.filter((o) => !o.suppressed)
    for (const op of ops) await get().generateOne(op.id)
  },

  generateOne: async (opId) => {
    const s = get()
    const op = s.job.operations.find((o) => o.id === opId)
    if (!op || !s.job.part) return
    set((st) => patchOp(st, opId, { regenState: 'running' }))
    try {
      const res = await api.generateOperation(
        s.job.part.upload_id,
        op,
        s.job.cell.tool?.id ?? null,
      )
      set((st) => {
        const stAfter = patchOp(st, opId, {
          regenState: 'ok',
          toolpath: res.toolpath,
          trajectory: res.trajectory ?? undefined,
          rapid: res.rapid ?? undefined,
          collision: res.collision ?? undefined,
        })
        return {
          ...stAfter,
          hasAnyToolpath: true,
          hasAnyTrajectory: !!res.trajectory,
          totalFrames: res.frameCount ?? st.totalFrames,
          messages: [...st.messages, ...res.messages],
        }
      })
    } catch (err) {
      set((st) => patchOp(st, opId, { regenState: 'error' }))
      console.error(err)
    }
  },

  exportArtifact: (kind) => {
    const s = get()
    const op = s.job.operations.find((o) => o[kind] != null)
    if (!op) return
    const data =
      kind === 'rapid'
        ? op.rapid ?? ''
        : JSON.stringify(op[kind], null, 2)
    const blob = new Blob([data], {
      type: kind === 'rapid' ? 'text/plain' : 'application/json',
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${op.label}_${kind}.${kind === 'rapid' ? 'mod' : 'json'}`
    a.click()
    URL.revokeObjectURL(url)
  },

  saveJob: () => {
    const job = get().job
    const blob = new Blob([JSON.stringify(job, null, 2)], { type: 'application/json' })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `${job.name}.amatl.json`
    a.click()
    URL.revokeObjectURL(url)
  },

  openJobDialog: () => {
    const inp = document.createElement('input')
    inp.type = 'file'
    inp.accept = '.json,.amatl.json'
    inp.onchange = async () => {
      const f = inp.files?.[0]
      if (!f) return
      const txt = await f.text()
      try {
        const job = JSON.parse(txt) as Job
        set({ job, selection: null, messages: [], scrubFrame: 0, totalFrames: 0 })
      } catch (err) {
        console.error('Not a valid .amatl.json:', err)
      }
    }
    inp.click()
  },

  openPrefs: () => set({ openDialog: 'prefs' }),
  openAbout: () => set({ openDialog: 'about' }),
  openToolImport: () => {
    set({ selection: { kind: 'tool' }, openDialog: 'toolImport' })
  },

  selectTreeNode: (nodeId) => {
    if (nodeId === 'part') set({ selection: { kind: 'part' } })
    else if (nodeId === 'cell') set({ selection: { kind: 'cell' } })
    else if (nodeId === 'cell/tool') set({ selection: { kind: 'tool' } })
    else if (nodeId === 'wobj') set({ selection: { kind: 'wobj' } })
    else if (nodeId.startsWith('operations/')) {
      set({ selection: { kind: 'operation', id: nodeId.slice('operations/'.length) } })
    }
  },

  togglePickMode: (opId) =>
    set((s) => ({ pickMode: s.pickMode === opId ? null : opId })),

  pickFace: (faceIndex: number) =>
    set((s) => {
      if (!s.pickMode) return s
      const op = s.job.operations.find((o) => o.id === s.pickMode)
      if (!op) return s
      if (op.kind === 'clad') {
        const faces = op.faces.includes(faceIndex)
          ? op.faces.filter((f) => f !== faceIndex)
          : [...op.faces, faceIndex]
        return patchOp(s, op.id, { faces, regenState: 'needsRegen' })
      }
      if (op.kind === 'seam') {
        // Two-click seam picker: pending holds the first pick, second
        // click completes a pair.
        const pending = s._seamPending
        if (pending === null || pending === undefined) {
          return { ...s, _seamPending: faceIndex }
        }
        if (pending === faceIndex) return { ...s, _seamPending: null }
        return {
          ...patchOp(s, op.id, {
            seamPairs: [...op.seamPairs, { a: pending, b: faceIndex }],
            regenState: 'needsRegen',
          }),
          _seamPending: null,
        }
      }
      return s
    }),

  updateOperation: (opId, patch) =>
    set((s) => patchOp(s, opId, { ...patch, regenState: 'needsRegen' })),

  commitPendingOperation: () => set({ pickMode: null }),
  cancelPendingOperation: () => set({ pickMode: null }),

  duplicateOperation: (opId) =>
    set((s) => {
      const op = s.job.operations.find((o) => o.id === opId)
      if (!op) return s
      const copy = { ...op, id: newId('op'), label: `${op.label} (copy)`, regenState: 'needsRegen' as RegenState }
      return { job: { ...s.job, operations: [...s.job.operations, copy] } }
    }),

  toggleSuppress: (opId) =>
    set((s) => patchOp(s, opId, { suppressed: !s.job.operations.find((o) => o.id === opId)?.suppressed })),

  deleteOperation: (opId) =>
    set((s) => ({
      job: { ...s.job, operations: s.job.operations.filter((o) => o.id !== opId) },
      selection:
        s.selection?.kind === 'operation' && s.selection.id === opId ? null : s.selection,
    })),

  setActiveTool: (toolId) =>
    set((s) => ({
      job: {
        ...s.job,
        cell: {
          ...s.job.cell,
          tool: toolId ? s.toolLibrary.find((t) => t.id === toolId) ?? null : null,
        },
        operations: markAllNeedsRegen(s.job.operations),
      },
    })),

  updateTool: (toolId, patch) =>
    set((s) => ({
      toolLibrary: s.toolLibrary.map((t) => (t.id === toolId ? { ...t, ...patch } : t)),
      job: {
        ...s.job,
        cell: {
          ...s.job.cell,
          tool: s.job.cell.tool?.id === toolId ? { ...s.job.cell.tool, ...patch } : s.job.cell.tool,
        },
      },
    })),

  togglePlay: () => {
    const s = get()
    if (s.playing) {
      if (s._playRaf) cancelAnimationFrame(s._playRaf)
      set({ playing: false, _playRaf: null })
      return
    }
    const start = performance.now()
    const startFrame = s.scrubFrame
    const fps = 60 // frames of the trajectory per second of wall clock
    const tick = () => {
      const now = performance.now()
      const st = get()
      const dt = (now - start) / 1000
      const f = Math.min(st.totalFrames - 1, Math.round(startFrame + dt * fps * st.playSpeed))
      if (f >= st.totalFrames - 1) {
        set({ scrubFrame: st.totalFrames - 1, playing: false, _playRaf: null })
        return
      }
      set({ scrubFrame: f })
      set({ _playRaf: requestAnimationFrame(tick) })
    }
    set({ playing: true, _playRaf: requestAnimationFrame(tick) })
  },
  stepFrame: (d) =>
    set((s) => ({
      scrubFrame: Math.max(0, Math.min(s.totalFrames - 1, s.scrubFrame + d)),
    })),
  setScrubFrame: (f) => set({ scrubFrame: f }),
  setPlaySpeed: (sp) => set({ playSpeed: sp }),

  jumpToMessage: (id) =>
    set((s) => {
      const m = s.messages.find((x) => x.id === id)
      return m ? { scrubFrame: m.frame } : s
    }),

  loadToolLibrary: async () => {
    try {
      const tools = await api.listTools()
      set({ toolLibrary: tools })
      // Default the active tool on a fresh job: pick the Panasonic
      // ATU01042 (air-cooled "separate" type) if present, otherwise
      // any Panasonic torch, otherwise the first tool. The cell's
      // ABB IRB 4600-40/2.55 is already defaulted in EMPTY_JOB; this
      // closes the loop so a brand-new user sees a working cell on
      // boot without picking a tool from the dropdown.
      const s = get()
      if (!s.job.cell.tool && tools.length > 0) {
        const def =
          tools.find((t) => t.id === 'panasonic_ATU01042') ??
          tools.find((t) => t.vendor.toLowerCase().includes('panasonic')) ??
          tools[0]
        set({
          job: { ...s.job, cell: { ...s.job.cell, tool: def } },
        })
      }
    } catch (err) {
      console.warn('listTools failed (OK in Phase 1 if endpoint missing):', err)
    }
  },

  pingApi: async () => {
    try {
      await api.fetchHealth()
      set({ apiConnected: true })
    } catch {
      set({ apiConnected: false })
    }
  },

  setPreferences: (prefs) => {
    set({ preferences: prefs })
    try {
      localStorage.setItem('amatl.prefs', JSON.stringify(prefs))
    } catch {}
  },
  closeDialog: () => set({ openDialog: null }),

  _setJob: (job) => set({ job }),
}))

// Rehydrate preferences from localStorage on module load.
try {
  const raw = localStorage.getItem('amatl.prefs')
  if (raw) useStore.setState({ preferences: JSON.parse(raw) })
} catch {}

function markAllNeedsRegen(ops: Operation[]): Operation[] {
  return ops.map((o) => (o.regenState === 'ok' ? { ...o, regenState: 'needsRegen' as const } : o))
}

function patchOp(s: Store, opId: string, patch: Partial<Operation>): Store {
  return {
    ...s,
    job: {
      ...s.job,
      operations: s.job.operations.map((o) => (o.id === opId ? { ...o, ...patch } : o)),
    },
  }
}
